import React, { useState } from 'react';
import { 
  X, Mail, Phone, Calendar, MapPin, User, Users, 
  Gift, Award, Clock, ChevronRight, ArrowLeft,
  UserCheck, Home, Package, TrendingUp, Wrench,
  FileText, AlertTriangle, MessageCircle, ExternalLink,
  Edit2, Save, Building, Hash, Briefcase
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { StatusBadge, PaymentProgress } from './DataTables';
import { toast } from 'sonner';

// Timeline Event Component
function TimelineEvent({ event, isLast }) {
  const statusIcons = {
    nuevo_lead: UserCheck,
    registrado: UserCheck,
    contactado: Phone,
    prueba_agendada: Calendar,
    instalado: Home,
    activo: Clock,
    pago_1: TrendingUp,
    pago_2: TrendingUp,
    pago_3: TrendingUp,
    validado: Award,
    bonificacion_generada: Gift,
    bonificacion_aplicada: Gift,
    recompensa_entregada: Gift
  };

  const Icon = statusIcons[event.status] || statusIcons[event.type] || Clock;
  const date = new Date(event.date);
  const formattedDate = date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="flex gap-3 lg:gap-4">
      <div className="flex flex-col items-center">
        <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-zinc-400" />
        </div>
        {!isLast && <div className="w-px flex-1 bg-white/10 my-2" />}
      </div>
      <div className="flex-1 pb-4 lg:pb-6 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {event.status && <StatusBadge status={event.status} />}
          {event.label && !event.status && (
            <span className="text-xs text-zinc-300">{event.label}</span>
          )}
          <span className="text-[10px] lg:text-xs text-zinc-500">{formattedDate}</span>
        </div>
        {event.note && (
          <p className="text-xs lg:text-sm text-zinc-400 mt-1">{event.note}</p>
        )}
      </div>
    </div>
  );
}

// Quick Action Button
function QuickActionButton({ icon: Icon, label, onClick, variant = 'default', disabled }) {
  const variants = {
    default: 'bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white',
    primary: 'bg-white text-black hover:bg-zinc-200',
    success: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30',
    whatsapp: 'bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30',
    danger: 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className={`h-9 px-3 flex items-center gap-2 rounded-lg ${variants[variant]}`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-xs">{label}</span>
    </Button>
  );
}

// Client Profile Panel - Full screen on mobile with Editable Fields
export function ClientProfile({ client, onClose, onAddReferral, onUpdateClient }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});

  if (!client) return null;

  // Mock extended client data (preparado para Odoo)
  const extendedClient = {
    ...client,
    phone: client.phone || '+34 612 345 678',
    address: client.address || 'Calle Gran Vía 28, 28013 Madrid',
    equipment: client.equipment || 'MANKLAR CHAAK',
    installationDate: client.installation_date || client.created_at,
    serviceStatus: client.service_status || 'active',
    commercialAgent: client.commercial_agent || 'Por asignar',
    technician: client.technician || 'Por asignar',
    clientCode: client.client_code || `MNK-${client.id?.substring(0,8).toUpperCase() || '0000'}`,
    clientType: client.client_type || 'hogar',
    contractNumber: client.contract_number || `CONT-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}`,
    nextMaintenance: client.next_maintenance || new Date(new Date().setMonth(new Date().getMonth() + 6)).toISOString(),
    ...editedData
  };

  const handleCall = () => {
    window.open(`tel:${extendedClient.phone.replace(/\s/g, '')}`);
    toast.success('Abriendo teléfono...');
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Hola ${client.name}, te contactamos desde Manklar.`);
    window.open(`https://wa.me/${extendedClient.phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    toast.success('Abriendo WhatsApp...');
  };

  const handleEmail = () => {
    window.open(`mailto:${client.email}?subject=Manklar - Información de tu servicio`);
    toast.success('Abriendo cliente de email...');
  };

  const handleViewContract = () => {
    toast.info('Funcionalidad disponible con integración Odoo', {
      description: 'Se conectará con el módulo de contratos de Odoo'
    });
  };

  const handleViewInvoices = () => {
    toast.info('Funcionalidad disponible con integración Odoo', {
      description: 'Se conectará con el módulo de facturación de Odoo'
    });
  };

  const handleCreateIncident = () => {
    toast.info('Funcionalidad disponible con integración Odoo', {
      description: 'Se conectará con el módulo de tickets/incidencias de Odoo'
    });
  };

  const handleScheduleMaintenance = () => {
    toast.info('Funcionalidad disponible con integración Odoo', {
      description: 'Se conectará con el módulo de mantenimientos de Odoo'
    });
  };

  const handleSaveChanges = () => {
    // In a real app, this would call the API
    toast.success('Cambios guardados', {
      description: 'Los datos se actualizarán con Odoo cuando esté integrado'
    });
    setIsEditing(false);
    if (onUpdateClient) {
      onUpdateClient({ ...client, ...editedData });
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 lg:inset-y-0 lg:right-0 lg:left-auto w-full lg:max-w-lg bg-[#0A0A0A] lg:border-l border-white/10 shadow-2xl z-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
        <button 
          onClick={onClose}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Volver</span>
        </button>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="text-zinc-400 hover:text-white hover:bg-white/10"
              data-testid="edit-client-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Editar
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleSaveChanges}
              className="bg-white text-black hover:bg-zinc-200"
              data-testid="save-client-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-zinc-400 hover:text-white lg:flex hidden"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Profile Header */}
        <div className="p-4 lg:p-6 border-b border-white/10">
          <div className="flex items-start gap-3 lg:gap-4">
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              <span className="text-lg lg:text-2xl font-light text-white">
                {client.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <Input
                  value={editedData.name || client.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  className="mb-2 h-8 bg-white/5 border-white/20 text-white text-lg font-medium"
                />
              ) : (
                <h2 className="text-lg lg:text-xl font-medium text-white font-['Outfit'] truncate">
                  {extendedClient.name || client.name}
                </h2>
              )}
              <p className="text-xs lg:text-sm text-zinc-500 mt-1 truncate">{client.email}</p>
              <div className="flex items-center gap-2 mt-2 lg:mt-3 flex-wrap">
                <span className="px-2 lg:px-3 py-1 rounded-full bg-white/10 text-white text-xs lg:text-sm font-mono">
                  {client.referral_code}
                </span>
                <span className="px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs">
                  {extendedClient.serviceStatus === 'active' ? 'Activo' : extendedClient.serviceStatus}
                </span>
                <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs">
                  {extendedClient.clientType === 'hogar' ? 'Hogar' : 'Empresa'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-4 lg:p-6 border-b border-white/10">
          <h3 className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-3">
            Acciones Rápidas
          </h3>
          <div className="flex flex-wrap gap-2">
            <QuickActionButton icon={Phone} label="Llamar" onClick={handleCall} />
            <QuickActionButton icon={MessageCircle} label="WhatsApp" onClick={handleWhatsApp} variant="whatsapp" />
            <QuickActionButton icon={Mail} label="Email" onClick={handleEmail} />
            <QuickActionButton icon={FileText} label="Contrato" onClick={handleViewContract} />
            <QuickActionButton icon={FileText} label="Facturas" onClick={handleViewInvoices} />
            <QuickActionButton icon={AlertTriangle} label="Incidencia" onClick={handleCreateIncident} variant="danger" />
            <QuickActionButton icon={Wrench} label="Mantenimiento" onClick={handleScheduleMaintenance} />
          </div>
        </div>

        {/* Extended Client Info - Odoo Ready */}
        <div className="p-4 lg:p-6 border-b border-white/10">
          <h3 className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-3 lg:mb-4">
            Información del Cliente
          </h3>
          <div className="space-y-3">
            <InfoField 
              icon={Hash} 
              label="Código de Cliente" 
              value={extendedClient.clientCode} 
              mono 
            />
            <InfoField 
              icon={Phone} 
              label="Teléfono" 
              value={extendedClient.phone}
              editable={isEditing}
              onChange={(val) => handleFieldChange('phone', val)}
            />
            <InfoField 
              icon={Mail} 
              label="Email" 
              value={client.email}
            />
            <InfoField 
              icon={MapPin} 
              label="Dirección" 
              value={extendedClient.address}
              editable={isEditing}
              onChange={(val) => handleFieldChange('address', val)}
            />
            <InfoField 
              icon={Package} 
              label="Equipo Contratado" 
              value={extendedClient.equipment}
            />
            <InfoField 
              icon={FileText} 
              label="Nº Contrato" 
              value={extendedClient.contractNumber}
              mono
            />
            <InfoField 
              icon={Calendar} 
              label="Fecha de Alta" 
              value={new Date(client.created_at).toLocaleDateString('es-ES', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            />
            <InfoField 
              icon={Home} 
              label="Fecha de Instalación" 
              value={new Date(extendedClient.installationDate).toLocaleDateString('es-ES', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            />
            <InfoField 
              icon={Wrench} 
              label="Próximo Mantenimiento" 
              value={new Date(extendedClient.nextMaintenance).toLocaleDateString('es-ES', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            />
            
            {/* Commercial & Tech Assignment */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="p-3 rounded-lg bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase className="w-3 h-3 text-zinc-500" />
                  <p className="text-[10px] text-zinc-500">Comercial</p>
                </div>
                {isEditing ? (
                  <Input
                    value={editedData.commercialAgent || extendedClient.commercialAgent}
                    onChange={(e) => handleFieldChange('commercialAgent', e.target.value)}
                    className="h-7 text-xs bg-white/5 border-white/20 text-white"
                  />
                ) : (
                  <p className="text-sm text-white">{extendedClient.commercialAgent}</p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Wrench className="w-3 h-3 text-zinc-500" />
                  <p className="text-[10px] text-zinc-500">Técnico</p>
                </div>
                {isEditing ? (
                  <Input
                    value={editedData.technician || extendedClient.technician}
                    onChange={(e) => handleFieldChange('technician', e.target.value)}
                    className="h-7 text-xs bg-white/5 border-white/20 text-white"
                  />
                ) : (
                  <p className="text-sm text-white">{extendedClient.technician}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="p-4 lg:p-6 border-b border-white/10">
          <h3 className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-3 lg:mb-4">
            Estadísticas de Referidos
          </h3>
          <div className="grid grid-cols-3 gap-2 lg:gap-4">
            <div className="p-3 lg:p-4 rounded-lg bg-white/5">
              <Users className="w-4 h-4 lg:w-5 lg:h-5 text-zinc-400 mb-1 lg:mb-2" />
              <p className="text-xl lg:text-2xl font-light text-white font-['Outfit']">
                {client.total_referrals}
              </p>
              <p className="text-[10px] lg:text-xs text-zinc-500">Referidos</p>
            </div>
            <div className="p-3 lg:p-4 rounded-lg bg-white/5">
              <Award className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-400 mb-1 lg:mb-2" />
              <p className="text-xl lg:text-2xl font-light text-white font-['Outfit']">
                {client.validated_referrals}
              </p>
              <p className="text-[10px] lg:text-xs text-zinc-500">Validados</p>
            </div>
            <div className="p-3 lg:p-4 rounded-lg bg-white/5">
              <Gift className="w-4 h-4 lg:w-5 lg:h-5 text-amber-400 mb-1 lg:mb-2" />
              <p className="text-xl lg:text-2xl font-light text-white font-['Outfit']">
                {client.available_rewards}
              </p>
              <p className="text-[10px] lg:text-xs text-zinc-500">Bonific.</p>
            </div>
          </div>
        </div>

        {/* Referrals List */}
        <div className="p-4 lg:p-6">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500">
              Referidos de este cliente
            </h3>
            <Button
              size="sm"
              onClick={() => onAddReferral?.(client)}
              className="h-7 lg:h-8 text-xs bg-white text-black hover:bg-zinc-200 rounded"
            >
              + Agregar
            </Button>
          </div>

          {client.referrals && client.referrals.length > 0 ? (
            <div className="space-y-2 lg:space-y-3">
              {client.referrals.map((referral) => (
                <div 
                  key={referral.id}
                  className="p-3 lg:p-4 rounded-lg bg-white/5 border border-white/10"
                >
                  <div className="flex items-center justify-between mb-2 lg:mb-3 gap-2">
                    <div className="min-w-0">
                      <p className="text-xs lg:text-sm font-medium text-white truncate">{referral.name}</p>
                      <p className="text-[10px] lg:text-xs text-zinc-500 truncate">{referral.email || 'Sin email'}</p>
                    </div>
                    <StatusBadge status={referral.status} compact />
                  </div>
                  <PaymentProgress payments={referral.payments_made} compact />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 lg:py-8">
              <Users className="w-8 h-8 lg:w-10 lg:h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-xs lg:text-sm text-zinc-500">Sin referidos aún</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Reusable Info Field Component
function InfoField({ icon: Icon, label, value, mono = false, editable = false, onChange }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-zinc-500">{label}</p>
        {editable ? (
          <Input
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            className="h-7 mt-1 text-sm bg-white/5 border-white/20 text-white"
          />
        ) : (
          <p className={`text-sm text-white ${mono ? 'font-mono' : ''}`}>{value}</p>
        )}
      </div>
    </div>
  );
}

// Referral Profile Panel - Full screen on mobile
export function ReferralProfile({ referral, onClose, onAddPayment, loadingPayment }) {
  if (!referral) return null;

  // Build complete history including payments
  const buildHistory = () => {
    const history = [...(referral.status_history || [])];
    
    // Add payment events based on payments_made
    if (referral.payments_made >= 1) {
      const baseDate = new Date(referral.created_at);
      baseDate.setMonth(baseDate.getMonth() + 1);
      history.push({
        type: 'pago_1',
        label: '1ª mensualidad abonada',
        date: baseDate.toISOString(),
        note: 'Primera mensualidad registrada'
      });
    }
    if (referral.payments_made >= 2) {
      const baseDate = new Date(referral.created_at);
      baseDate.setMonth(baseDate.getMonth() + 2);
      history.push({
        type: 'pago_2',
        label: '2ª mensualidad abonada',
        date: baseDate.toISOString(),
        note: 'Segunda mensualidad registrada'
      });
    }
    if (referral.payments_made >= 3) {
      const baseDate = new Date(referral.created_at);
      baseDate.setMonth(baseDate.getMonth() + 3);
      history.push({
        type: 'pago_3',
        label: '3ª mensualidad abonada',
        date: baseDate.toISOString(),
        note: 'Tercera mensualidad registrada'
      });
      // Add validation event
      history.push({
        status: 'validado',
        date: baseDate.toISOString(),
        note: 'Referido validado automáticamente'
      });
      // Add bonus generation
      history.push({
        type: 'bonificacion_generada',
        label: 'Bonificación generada',
        date: baseDate.toISOString(),
        note: '+1 mes gratis para el cliente que refirió'
      });
    }
    
    // Sort by date
    return history.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const history = buildHistory();

  return (
    <div className="fixed inset-0 lg:inset-y-0 lg:right-0 lg:left-auto w-full lg:max-w-lg bg-[#0A0A0A] lg:border-l border-white/10 shadow-2xl z-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
        <button 
          onClick={onClose}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Volver</span>
        </button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-zinc-400 hover:text-white lg:flex hidden"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Profile Header */}
        <div className="p-4 lg:p-6 border-b border-white/10">
          <div className="flex items-start gap-3 lg:gap-4">
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              <span className="text-lg lg:text-2xl font-light text-white">
                {referral.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg lg:text-xl font-medium text-white font-['Outfit'] truncate">
                {referral.name}
              </h2>
              <div className="mt-2">
                <StatusBadge status={referral.status} />
              </div>
            </div>
            {referral.status === 'validado' && (
              <span className="text-2xl lg:text-3xl flex-shrink-0">🎁</span>
            )}
          </div>
        </div>

        {/* Contact Info */}
        <div className="p-4 lg:p-6 border-b border-white/10 space-y-2 lg:space-y-3">
          <h3 className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-3 lg:mb-4">
            Información de Contacto
          </h3>
          <div className="flex items-center gap-3 text-xs lg:text-sm">
            <Mail className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <span className="text-zinc-300 truncate">{referral.email || 'Sin email'}</span>
          </div>
          {referral.phone && (
            <div className="flex items-center gap-3 text-xs lg:text-sm">
              <Phone className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              <span className="text-zinc-300">{referral.phone}</span>
            </div>
          )}
          <div className="flex items-center gap-3 text-xs lg:text-sm">
            <Calendar className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <span className="text-zinc-300">
              Registrado el {new Date(referral.created_at).toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>

        {/* Referrer Info */}
        <div className="p-4 lg:p-6 border-b border-white/10">
          <h3 className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-3 lg:mb-4">
            Referido por
          </h3>
          <div className="flex items-center gap-3 lg:gap-4 p-3 lg:p-4 rounded-lg bg-white/5">
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 lg:w-5 lg:h-5 text-zinc-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs lg:text-sm font-medium text-white truncate">{referral.referrer_name}</p>
              <p className="text-[10px] lg:text-xs text-zinc-500 font-mono">{referral.referrer_code}</p>
            </div>
          </div>
        </div>

        {/* Payment Progress */}
        <div className="p-4 lg:p-6 border-b border-white/10">
          <h3 className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-3 lg:mb-4">
            Progreso de Mensualidades
          </h3>
          <div className="p-3 lg:p-4 rounded-lg bg-white/5">
            <PaymentProgress 
              payments={referral.payments_made}
              onAddPayment={referral.payments_made < 3 ? () => onAddPayment?.(referral.id) : null}
              loading={loadingPayment === referral.id}
              buttonLabel="Registrar mensualidad"
            />
            <p className="text-[10px] lg:text-xs text-zinc-500 mt-2 lg:mt-3">
              {referral.payments_made < 3 
                ? `Faltan ${3 - referral.payments_made} ${3 - referral.payments_made === 1 ? 'mensualidad' : 'mensualidades'} para validar`
                : '¡Referido validado! Bonificación generada automáticamente'
              }
            </p>
          </div>
        </div>

        {/* History Timeline */}
        <div className="p-4 lg:p-6">
          <h3 className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-3 lg:mb-4">
            Historial de Eventos
          </h3>
          {history.length > 0 ? (
            <div>
              {history.map((event, index) => (
                <TimelineEvent 
                  key={index} 
                  event={event} 
                  isLast={index === history.length - 1}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-6 lg:py-8">
              <Clock className="w-8 h-8 lg:w-10 lg:h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-xs lg:text-sm text-zinc-500">Sin historial disponible</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientProfile;
